'use strict';

import axios from 'axios';
import store from '@/store';
import { isAuthorizationRequiredUrl, API_ACCESS_TOKEN_PRE_REFRESH_TIME, PLATFORM_API, LOGIN_RENEWAL_API_URL, defaultApiErrorHandler } from '@/api.js';
import { ADMIN_LOGIN_URL } from '@/constants.js';

// Full config:  https://github.com/axios/axios#request-config
// axios.defaults.baseURL = process.env.baseURL || process.env.apiUrl || '';
// axios.defaults.headers.common['Authorization'] = AUTH_TOKEN;
/**
 * 后端依此标识为异步请求
 */
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.withCredentials = true; //Cookie跨域处理
axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
let config = {
    // baseURL: process.env.baseURL || process.env.apiUrl || ""
    // timeout: 60 * 1000, // Timeout
    // withCredentials: true, // Check cross-site Access-Control
};

/**
 * API接口访问使用的axios
 */
const _axios = axios.create(config);

/**
 * 刷新accessToken的信号量(防止多个请求同时触发了刷新accessToken的问题)
 */
_axios.accessTokenRefreshing = false;

/**
 * 校验accessToken的剩余有效时间，并按需要刷新accessToken(自动续约)
 * @param {*} apiUrl 
 */
function validateAccessToken(apiUrl) {
    //console.log('>>> apiUrl = ' + apiUrl);
    let accessToken = store.getters.getAccessToken(); //获取最新的accessToken
    if (accessToken.access_token && accessToken.acquired_time) {
        let remains = accessToken.expires_in - (new Date().getTime() - accessToken.acquired_time) / 1000; //剩余有效时间(秒)
        //console.log(`>>> remains = ${remains}, apiUrl = ${apiUrl}`);
        if (remains > 0 && remains < API_ACCESS_TOKEN_PRE_REFRESH_TIME) { //如果真的是长久不操作导致的token失效则不予自动续约
            if (!_axios.accessTokenRefreshing) {
                _axios.accessTokenRefreshing = true; //设置刷新状态为true
                console.log(`>>> Prepare renewal login accessToken: ${accessToken.access_token}`);
                //如果使用axios异步框架来请求续约接口那么请确保后台支持：在续约成功后的短时间内新旧token都是有效的,否则请引入webpack-zepto利用ajax的同步选项阻塞当前JS线程
                _axios.post(PLATFORM_API.of(LOGIN_RENEWAL_API_URL)).then(response => {
                    let result = response.data;
                    accessToken = result.data;
                    if (accessToken && accessToken.access_token) {
                        accessToken.acquired_time = new Date().getTime();
                        store.commit('updateAccessToken', accessToken);
                        console.log(`<<< Succeed renewal login accessToken: ${accessToken.access_token}`);
                    } else {
                        console.error(`<<< Failed to renewal login accessToken: ${result.message}`);
                    }
                }).catch(defaultApiErrorHandler).then(() => {
                    _axios.accessTokenRefreshing = false; //最后复位信号量
                });
            }
        }
    }
}

/**
 * 更新用户访问状态
 * @param {*} apiUrl 
 */
function updateUserAccessState(apiUrl) {
    const userAccessState = store.getters.getUserAccessState();
    userAccessState.userName = store.state.loginUserAllData.loginUser.userName; //记录访问用户
    userAccessState.secondLastAccessApiUrl = userAccessState.lastAccessApiUrl; //记录倒数第二访问的API接口URL
    userAccessState.secondLastAccessTime = userAccessState.lastAccessTime; //倒数第二访问时间点
    userAccessState.lastAccessApiUrl = apiUrl; //记录最后访问的API接口URL
    userAccessState.lastAccessTime = new Date().getTime(); //最后访问时间点
    store.dispatch('executeUpdateUserAccessState', userAccessState); //更新用户访问状态
}

//添加全局请求拦截器
_axios.interceptors.request.use(
    function(config) {
        let apiUrl = config.url.substring(config.url.indexOf('/api/'));
        //console.log('>>> apiUrl = ' + apiUrl);
        updateUserAccessState(apiUrl);
        // Do something before request is sent
        if (isAuthorizationRequiredUrl(apiUrl)) { //当前请求需要携带'Authorization'头?
            if (apiUrl != LOGIN_RENEWAL_API_URL) { //如果当前请求不是续约请求
                validateAccessToken(apiUrl); //先校验accessToken
            }
            let accessToken = store.getters.getAccessToken();
            if (accessToken && accessToken.access_token) {
                console.log(`>>> Apply 'Authorization'(${accessToken.access_token}) for path: ${config.url}`);
                config.headers['Authorization'] = 'bearer ' + accessToken.access_token;
            }
        }
        return config;
    },
    function(error) {
        // Do something with request error
        return Promise.reject(error);
    }
);

/**
 * 特定处理的响应错误发生了(防止401/403等相同错误消息冒泡弹出)
 */
_axios.specialResponseErrorArised = false;

//添加全局响应拦截器
_axios.interceptors.response.use(
    function(response) {
        // Do something with response data
        return response;
    },
    function(error) {
        if (error.response) {
            // Do something with response error
            let statusCode = error.response.status;
            if (statusCode == 401 || statusCode == 403 || statusCode == 503) {
                if (!_axios.specialResponseErrorArised) {
                    _axios.specialResponseErrorArised = true;
                    let app = window.app;
                    if (app) {
                        if (app.$Progress) {
                            app.$Progress.fail();
                        }
                        app.$message.error(app.$i18n.t('message.httpstatus.' + statusCode));
                    }
                    if (statusCode == 401) { //登录令牌失效或者为空
                        setTimeout(() => {
                            //console.log(ADMIN_LOGIN_URL);
                            window.location.href = ADMIN_LOGIN_URL;
                        }, 3000);
                    }
                }
                return Promise.reject();
            }
        }
        return Promise.reject(error);
    }
);

export default {
    install(Vue, options) {
        Vue.axios = _axios;
        window.axios = _axios;
        Object.defineProperties(Vue.prototype, {
            axios: {
                get() {
                    return _axios;
                }
            },
            $http: {
                get() {
                    return _axios;
                }
            }
        });
    }
};